/**
 * Utility functions for calculating and generating school year options
 * School years run from September 1st to August 31st
 */

/**
 * Calculate the current school year based on a given date
 * @param {Date} date - The date to calculate from (defaults to current date)
 * @returns {number} The starting year of the current school year
 *
 * Examples:
 * - March 15, 2025 → 2024 (school year 24/25)
 * - October 1, 2025 → 2025 (school year 25/26)
 */
export const getCurrentSchoolYear = (date = new Date()) => {
  const year = date.getFullYear();
  const month = date.getMonth(); // 0-based (September = 8)

  // If we're before September, we're still in the previous school year
  if (month < 8) {
    // January through August
    return year - 1;
  }

  // September through December - we're in the current calendar year's school year
  return year;
};

/**
 * Format a school year number into display text
 * @param {number} year - The starting year of the school year
 * @returns {string} Formatted school year text (e.g., "24/25")
 */
export const formatSchoolYear = (year) => {
  const startYear = year.toString().slice(-2); // Last 2 digits
  const endYear = (year + 1).toString().slice(-2); // Last 2 digits of next year
  return `${startYear}/${endYear}`;
};

/**
 * Generate an array of school year options for the attendance dropdown
 * Returns current school year plus the specified number of previous years
 * @param {number} previousYears - Number of previous years to include (default: 3)
 * @param {Date} fromDate - Date to calculate current year from (default: today)
 * @returns {Array} Array of year objects with key, value, and text properties
 */
export const generateSchoolYearOptions = (
  previousYears = 3,
  fromDate = new Date()
) => {
  const currentYear = getCurrentSchoolYear(fromDate);
  const years = [];

  // Generate years from (current - previousYears) to current
  for (let i = previousYears; i >= 0; i--) {
    const year = currentYear - i;
    years.push({
      key: year.toString(),
      value: year.toString(),
      text: formatSchoolYear(year),
    });
  }

  return years;
};

/**
 * Get the default selected year (most recent/current)
 * @param {Date} fromDate - Date to calculate from (default: today)
 * @returns {string} The year value to use as default selection
 */
export const getDefaultSchoolYear = (fromDate = new Date()) => {
  return getCurrentSchoolYear(fromDate).toString();
};
